import React, {useCallback, useEffect, useRef, useState} from "react";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {selectedColonicalBookId, selectedColonicalBookInfo, selectedColonicalTableauId} from "../../../recoils/recoil";
import {useGetTocsWithTablesByBookId} from "./useGetTocsWithTablesByBookId";
import {IlamListData} from "./IlamListData";
import {Link} from "react-router-dom";
import Tableau from "./Tableau";
import ReactModal from "react-modal";

/**
 * 아카이브 > 조선제학교 일람 > 목차
 * @param backToList 뒤로 가기 함수
 * @param getBookInfo 책 정보 전달
 * @returns
 * 목록과 목차가 같이 있음. 조건에 따라 목록을 보여줄지 목차를 보여줄지 나누어짐
 */
const ColonicalSchoolToc = ({backToList}) => {

    const ITEMS_PER_PAGE = 6;
    const [, setPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [filteredDatas, setFilteredDatas] = useState([]);
    const [expandedItems, setExpandedItems] = useState({});
    const [selectedTableauId, setSelectedTableauId] = useRecoilState(selectedColonicalTableauId);
    const bookId = useRecoilValue(selectedColonicalBookId);
    const {tocDatas, } = useGetTocsWithTablesByBookId(bookId);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);

    const setBookInfo = useSetRecoilState(selectedColonicalBookInfo)

    useEffect(() => {
        if (tocDatas.length > 0) {
            const initialData = tocDatas.slice(0, ITEMS_PER_PAGE);
            setFilteredDatas(initialData);
            setHasNextPage(tocDatas.length > ITEMS_PER_PAGE);
        }

        fetchAllIlamListData().then(data => {
            const bookInfoArray = data.filter(item => item.book_id === bookId);
            // 리코일에 넣어보기
            setBookInfo(bookInfoArray[0]);

        });
    }, [tocDatas, bookId, setBookInfo]);

    const loadMoreData = useCallback(() => {
        if (loadingRef.current || !hasNextPage) return;

        loadingRef.current = true;
        const startIndex = filteredDatas.length;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const newData = tocDatas.slice(startIndex, endIndex);

        if (newData.length > 0) {
            setFilteredDatas((prev) => [...prev, ...newData]);
            setHasNextPage(endIndex < tocDatas.length);
            setPage((prev) => prev + 1);
        } else {
            setHasNextPage(false);
        }

        loadingRef.current = false;
    }, [filteredDatas, hasNextPage, tocDatas]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && hasNextPage && !loadingRef.current) {
                    loadMoreData();
                }
            },
            {
                root: null,
                rootMargin: '20px',
                threshold: 0.1
            }
        );

        const currentObserver = observerRef.current;
        if (currentObserver) {
            observer.observe(currentObserver);
        }

        return () => {
            if (currentObserver) {
                observer.unobserve(currentObserver);
            }
        };
    }, [hasNextPage, filteredDatas, loadMoreData]);

    const fetchAllIlamListData = async () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(IlamListData);
            }, 500);
        });
    };

    const handleToggle = (tableId) => {
        setExpandedItems((prev) => ({
            ...prev,
            [tableId]: !prev[tableId],
        }));
    };

    const backToToc = () => {
        setSelectedTableauId(null);
    };

    return (
        <div className="cont-btm">
            <div className="list-wrap table-contents">
                <div className="w-set">
                    <div className="list-btm">
                        <div className="table-wrap">
                            <div className="table-top">
                                <strong>목차</strong>
                                <div className="back-list">
                                    <div className="back" onClick={backToList}>뒤로가기</div>
                                </div>
                            </div>
                            <div className="scroll-table fold-table">
                                {filteredDatas.map((item) => (
                                    <div className="list-row" key={item.toc_id}>
                                        <div className="table-title">
                                            <div className="title bold">{item.toc_title}</div>
                                            <div className="title-ko bold">{item.toc_title_ko}</div>
                                        </div>
                                        <div className="sub-table-wrap">
                                            {item.tables && item.tables.length > 0 && (
                                                <div className="sub-list-row"
                                                     key={item.tables[0].tableau_id}>
                                                    <div
                                                        className={`table-title ${expandedItems[item.tables[0].tableau_id] ? 'on' : ''}`}
                                                        onClick={() => handleToggle(item.tables[0].tableau_id)}>
                                                        <div className="title">
                                                            <span className="hierarchy1">{item.toc_title}</span>
                                                            <span className="hierarchy2">{item.tables[0].tableau_title}</span>
                                                        </div>
                                                        <div className="title-ko">
                                                            <span className="hierarchy1">{item.toc_title_ko}</span>
                                                            <span className="hierarchy2">{item.tables[0].tableau_title_ko}</span>
                                                            <div className="view-count">
                                                                ({item.tables.length})
                                                            </div>
                                                        </div>
                                                        <i className="i-toggle"></i>
                                                    </div>
                                                    <div
                                                        className="sub-table-view"
                                                        style={{
                                                            height: expandedItems[item.tables[0].tableau_id]
                                                                ? `${item.tables.length * 50}px` // Adjust the height dynamically based on the content
                                                                : '0',
                                                            overflow: 'hidden',
                                                            transition: 'height 0.3s ease',
                                                        }}
                                                    >
                                                        <div className="view-wrap">
                                                            {item.tables.map((subItem) => (
                                                                <div className="view-row"
                                                                     key={subItem.tableau_id}>
                                                                    <Link to="#" onClick={() => setSelectedTableauId(subItem.tableau_id)}>
                                                                        <div
                                                                            className="view-title">{subItem.tableau_title}</div>
                                                                        <div
                                                                            className="view-title-ko">{subItem.tableau_title_ko}</div>
                                                                        <i className="i-view"></i>
                                                                    </Link>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                        {hasNextPage && <div ref={observerRef}  />}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

             {/*조선제학교일람 표 팝업 */}
            <ReactModal
                isOpen={selectedTableauId !== null}
                onRequestClose={backToToc}
                contentLabel="조선제학교 일람 표"
                style={{
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1000,
                    },
                    content: {
                        inset: "unset",
                        padding: 0,
                        border: "none",
                        borderRadius: 0,
                        background: "transparent",
                        width: "auto",
                        height: "auto",
                    },
                }}
            >
            <div className={`wbz-popup-cont is-template type-table-full ${selectedTableauId !== null ? 'on' : ''}`} id="colonialSchoolTablePopup"
            >
                <Tableau backToToc={backToToc}/>
            </div>
            </ReactModal>
        </div>

    );
};

export default ColonicalSchoolToc;